import React, { useEffect } from 'react';
import { Modal, Form, Input, message } from 'antd';
import { useTranslation } from '@refinedev/core';
import { endPoints } from '@/api/configs';
import useAPI from '@/api/useApi';
import { AxiosError } from 'axios';

interface EditEmbedPlatformModalProps {
  open: boolean;
  onCancel: () => void;
  refetch: () => void;
  currentName: string;
}

export const EditEmbedPlatformModal: React.FC<EditEmbedPlatformModalProps> = ({
  open,
  onCancel,
  refetch,
  currentName,
}) => {
  const { translate } = useTranslation();
  const [form] = Form.useForm<{ name: string }>();
  const { usePatch } = useAPI();
  const { mutateAsync: patchSystemConfig, isLoading } = usePatch(
    endPoints.systemConfig
  );

  useEffect(() => {
    if (open) {
      form.setFieldsValue({ name: currentName });
    }
  }, [open, currentName, form]);

  const handleOk = async () => {
    const values = await form.validateFields();
    const newName = values.name.trim();

    if (newName === currentName) {
      onCancel();
      return;
    }

    try {
      await patchSystemConfig({
        operation: 'REMOVE',
        field: 'ALLOWED_EMBED_PLATFORM',
        value: currentName,
      });
      try {
        await patchSystemConfig({
          operation: 'ADD',
          field: 'ALLOWED_EMBED_PLATFORM',
          value: newName,
        });
        message.success(
          translate('system-config.embed-platforms.updated-success')
        );
        form.resetFields();
        refetch();
        onCancel();
      } catch (addError) {
        try {
          await patchSystemConfig({
            operation: 'ADD',
            field: 'ALLOWED_EMBED_PLATFORM',
            value: currentName,
          });
        } catch {
          // Best-effort restore if rename's ADD fails after REMOVE
        }
        message.error((addError as AxiosError).message);
      }
    } catch (removeError) {
      message.error((removeError as AxiosError).message);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };

  return (
    <Modal
      open={open}
      onCancel={handleCancel}
      onOk={handleOk}
      confirmLoading={isLoading}
      title={translate('system-config.embed-platforms.edit-title')}
      okText={translate('buttons.save')}
      cancelText={translate('buttons.cancel')}
    >
      <Form form={form} layout="vertical">
        <Form.Item
          name="name"
          label={translate('system-config.embed-platforms.name')}
          rules={[
            {
              required: true,
              message: translate('pages.errors.required'),
            },
          ]}
        >
          <Input
            placeholder={translate(
              'system-config.embed-platforms.name-placeholder'
            )}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};
